import { Component, ElementRef, Input, OnInit, ViewChild, Renderer2, OnDestroy } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { AIService } from 'src/app/services/ai/ai.service';
import { MixpanelService } from 'src/app/services/mixpanel/mixpanel.service';
import { ChatStateService, Message } from 'src/app/services/chat-state/chat-state.service';
import { SectionTooltipService } from 'src/app/services/section-tooltip/section-tooltip.service';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

// The API ends each general guidance block with a link to this anchor. The chat shows it
// like a citation that explains itself on hover; it is not a link to anywhere.
const NOT_VERIFIED_ANCHOR = '#not-verified-by-gps';
const NOT_VERIFIED_CLASS = 'not-verified';
const NOT_VERIFIED_LABEL = 'Not GPS-verified';
const NOT_VERIFIED_EXPLANATION = 'This section provides additional context that may be useful, ' +
    "but has not been reviewed by GPS's legal researchers";

@Component({
    selector: 'app-ai-global',
    templateUrl: './ai-global.component.html',
    styleUrls: ['./ai-global.component.scss'],
})
export class AIGlobalComponent implements OnInit, OnDestroy {
    userInput: string = '';
    @Input() useTextEntry: boolean = true;

    private hoverSubject = new Subject<{ url: string, x: number, y: number } | null>();
    private destroy$ = new Subject<void>();
    private linkListeners: (() => void)[] = [];
    private lastCompletedMessageCount: number = 0;

    @ViewChild('chatHistory') private chatHistory: ElementRef;
    @ViewChild('chatInput') private chatInput: ElementRef;

    constructor(
        private aiService: AIService,
        private sanitizer: DomSanitizer,
        private mixpanelService: MixpanelService,
        private renderer: Renderer2,
        public chatState: ChatStateService,
        private tooltipService: SectionTooltipService
    ) { }

    sendMessage() {
        if (this.chatState.loading && this.userInput.trim()) {
            this.chatState.addToQueue(this.userInput);
            this.userInput = '';
            return;
        }
        if (this.userInput.trim()) {
            if (!this.chatState.isChatStarted) {
                this.chatState.setIsChatStarted(true);
            }
            this.chatState.addMessage({ text: this.userInput, isUser: true, complete: false });
            this.chatState.setLoading(true);
            this.chatState.setWaiting(true);
            const new_message = { text: '' as SafeHtml, markdown: '', isUser: false, complete: false } as Message;

            this.chatState.addMessage(new_message);

            this.aiService.getResponse(this.userInput, this.chatState.thread).subscribe({
                next: async response => {
                    if (response) {
                        this.chatState.setWaiting(false);

                        new_message.markdown = response;

                        // Format as markdown and sanitize
                        // Keep the "Not verified" mark on the last line of its block: an empty quoted
                        // line before it would put it in a paragraph of its own
                        let markdown: string = await marked.parse(response.replace(
                            new RegExp(`(?:\\n>[ \\t]*)+\\n(>[ \\t]*\\[[^\\]]*\\]\\(${NOT_VERIFIED_ANCHOR}\\))`, 'g'), '\n$1'));

                        // No href, so clicking it does nothing
                        markdown = markdown.replace(
                            new RegExp(`<a href="${NOT_VERIFIED_ANCHOR}">[^<]*</a>`, 'g'),
                            `<a class="${NOT_VERIFIED_CLASS}">${NOT_VERIFIED_LABEL}</a>`);

                        // Make sure links open in a new tab
                        markdown = markdown.replace(/<a /g, '<a target="_blank"');

                        // TODO: Make sure we are sanitizing properly
                        const sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(markdown);

                        // Update the most recent message
                        new_message.text = sanitizedHtml;

                        // Trigger state update
                        this.chatState.setMessages([...this.chatState.messages]);
                    }
                }, error: (error) => {
                    console.error('Error:', error);
                    this.chatState.setLoading(false);
                    this.chatState.setWaiting(false);
                    new_message.text = 'Sorry, I am having trouble connecting to the server. Please try again later.';
                    this.chatState.setMessages([...this.chatState.messages]);
                }, complete: () => {
                    this.chatState.setWaiting(false);
                    this.chatState.setLoading(false);
                    new_message.complete = true;
                    this.chatState.setMessages([...this.chatState.messages]);
                    this.processQueue();
                    this.setFocusToChatInput();
                    this.mixpanelService.track('AI Chat Request');
                }
            });
            this.userInput = '';
        }
    }

    processQueue() {
        const nextMessage = this.chatState.shiftFromQueue();
        if (nextMessage) {
            this.userInput = nextMessage;
            this.sendMessage();
        }
    }


    public openChat(event: Event): void {
        this.chatState.setIsChatStarted(true);
        // Reset to force reattaching link listeners when chat reopens
        this.lastCompletedMessageCount = 0;
        event.preventDefault();
        setTimeout(() => {
            this.setFocusToChatInput();
        }, 0);
    }

    /**
     * Copy a response as formatted HTML (which Word, Outlook and Excel paste as headings, bold,
     * lists and tables) and as text, for editors that only take text. The sources are listed once
     * at the end instead of being cited inline.
     */
    public async copyToClipboard(markdown: string): Promise<void> {
        const { body, sources } = this.withoutCitations(markdown);
        const text = sources.length
            ? `${body}\n\nReferences\n${sources.map(source => `- ${source.label}: ${source.url}`).join('\n')}`
            : body;
        try {
            const formatted = sources.length
                ? `${body}\n\n**References**\n\n${sources.map(source => `- [${source.label}](${source.url})`).join('\n')}`
                : body;
            // Word pastes a table without borders unless the table asks for them
            const html = (await marked.parse(formatted)).replace(/<table>/g, '<table border="1" cellpadding="4" style="border-collapse: collapse">');
            await navigator.clipboard.write([new ClipboardItem({
                'text/html': new Blob([html], { type: 'text/html' }),
                'text/plain': new Blob([text], { type: 'text/plain' }),
            })]);
        } catch (error) {
            // Formatted content is not supported or not allowed here: copy the text alone
            try {
                await navigator.clipboard.writeText(text);
            } catch (textError) {
                console.error('Failed to copy to clipboard:', textError);
            }
        }
    }

    /**
     * Take the inline citations, their link definitions and the "Not verified by GPS" link out of
     * a response. Returns what is left and the sources, in the order they were first cited, with
     * absolute links so they work where they are pasted.
     */
    private withoutCitations(markdown: string): { body: string, sources: { label: string, url: string }[] } {
        const urls = new Map<string, string>();
        let body = markdown.replace(/^\[([^\]]+)\]:[ \t]*(\S+)[ \t]*\n?/gm, (_match, label: string, url: string) => {
            urls.set(label, url.startsWith('/') ? window.location.origin + url : url);
            return '';
        });
        body = body
            .replace(/^>?[ \t]*\[[^\]]*\]\(#not-verified-by-gps\)[ \t]*\n?/gm, '')
            .replace(/[ \t]*\[[^\]]*\]\(#not-verified-by-gps\)/g, '');

        const cited: string[] = [];
        body = body.replace(/[ \t]*\[([^\]]+)\](?![(:[])/g, (match, label: string) => {
            if (!urls.has(label)) {
                return match;
            }
            if (!cited.includes(label)) {
                cited.push(label);
            }
            return '';
        });
        const labels = [...cited, ...Array.from(urls.keys()).filter(label => !cited.includes(label))];
        return { body: body.trim(), sources: labels.map(label => ({ label, url: urls.get(label) as string })) };
    }

    private scrollToBottom(): void {
        if (this.chatHistory && this.chatState.loading) {
            try {
                this.chatHistory.nativeElement.scrollTop = this.chatHistory.nativeElement.scrollHeight;
            } catch (err) {
                console.error('Scroll to bottom failed', err);
            }
        }
    }

    private setFocusToChatInput(): void {
        if (this.chatInput) {
            try {
                this.chatInput.nativeElement.focus();
            } catch (err) {
                console.error('Set focus to chat input failed', err);
            }
        }
    }
    ngOnInit() {
        document.addEventListener('keydown', this.handleEscapeKey.bind(this));
        document.addEventListener('click', this.handleClickOutside.bind(this));

        // Handle hover events with debounce to avoid excessive API calls
        this.hoverSubject.pipe(
            takeUntil(this.destroy$),
            debounceTime(300)
        ).subscribe(data => {
            if (data) {
                this.showTooltip(data.url, data.x, data.y);
            } else if (!this.tooltipService.tooltipHovered) {
                this.tooltipService.hide();
            }
        });
    }

    ngOnDestroy() {
        document.removeEventListener('keydown', this.handleEscapeKey.bind(this));
        document.removeEventListener('click', this.handleClickOutside.bind(this));
        this.destroy$.next();
        this.destroy$.complete();
        this.cleanupLinkListeners();
    }

    private handleClickOutside(event: MouseEvent) {
        if (!this.tooltipService.visible) return;

        const target = event.target as HTMLElement;
        // Check if click is inside the tooltip
        if (target.closest('.section-tooltip')) return;
        // Check if click is on a citation link
        if (target.closest('.bot-message a')) return;

        this.tooltipService.hide();
    }

    private cleanupLinkListeners() {
        this.linkListeners.forEach(unlisten => unlisten());
        this.linkListeners = [];
    }

    private attachLinkListeners() {
        if (!this.chatHistory) return;

        // Only re-attach listeners when completed message count changes
        const completedCount = this.chatState.messages.filter(m => !m.isUser && m.complete).length;
        if (completedCount === this.lastCompletedMessageCount) {
            return;
        }
        this.lastCompletedMessageCount = completedCount;

        // Clean up existing listeners
        this.cleanupLinkListeners();

        // Find all links in bot messages
        const links = this.chatHistory.nativeElement.querySelectorAll('.bot-message a');

        links.forEach((link: HTMLElement) => {
            const mouseEnter = this.renderer.listen(link, 'mouseenter', () => {
                const href = link.classList.contains(NOT_VERIFIED_CLASS) ? NOT_VERIFIED_ANCHOR : link.getAttribute('href');
                if (href && (href.includes('/country/') || href === NOT_VERIFIED_ANCHOR)) {
                    const rect = link.getBoundingClientRect();
                    this.tooltipService.cancelHide();
                    this.hoverSubject.next({ url: href, x: rect.left, y: rect.bottom + 5 });
                }
            });

            const mouseLeave = this.renderer.listen(link, 'mouseleave', () => {
                this.hoverSubject.next(null);
                this.tooltipService.scheduleHide();
            });

            this.linkListeners.push(mouseEnter, mouseLeave);
        });
    }

    private showTooltip(url: string, x: number, y: number) {
        // Viewport adjustment for positioning
        const tooltipWidth = 600;
        // A section preview can fill the panel; the "Not verified" explanation is about two lines
        const notVerified = url === NOT_VERIFIED_ANCHOR;
        const tooltipMaxHeight = notVerified ? 70 : 400;
        const edgePadding = 40;
        const rightEdgePadding = 70;

        const viewportWidth = window.innerWidth;
        if (x + tooltipWidth + rightEdgePadding > viewportWidth) {
            x = Math.max(edgePadding, viewportWidth - tooltipWidth - rightEdgePadding);
        }

        const viewportHeight = window.innerHeight;
        if (y + tooltipMaxHeight + edgePadding > viewportHeight) {
            // Above the link instead: clear of the pill for the short panel
            y = Math.max(edgePadding, y - tooltipMaxHeight - (notVerified ? 35 : 20));
        }

        if (notVerified) {
            this.tooltipService.showText(NOT_VERIFIED_EXPLANATION, x, y);
        } else {
            this.tooltipService.showFromUrl(url, x, y);
        }
    }

    ngAfterViewChecked() {
        this.scrollToBottom();
        this.attachLinkListeners();
    }

    handleEscapeKey(event: KeyboardEvent) {
        if (event.key === 'Escape' && this.chatState.isChatStarted) {
            this.chatState.setIsChatStarted(false);
        }
    }

    closeOverlay(event: MouseEvent) {
        if ((event.target as HTMLElement).classList.contains('chat-overlay')) {
            this.chatState.setIsChatStarted(false);
        }
    }

}
